import React, { useCallback, useState, useRef } from 'react';
import Box from '@material-ui/core/Box';
import MenuItem from '@material-ui/core/MenuItem';
import Typography from '@material-ui/core/Typography';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { Form } from 'react-final-form';
import { FORM_ERROR, FormApi } from 'final-form';
import { useDataProvider, useRedirect } from 'react-admin';

import { Dialog } from 'components/common';
import { Switch } from 'components/form';
import ActionButton from 'components/buttons/ActionButton';
import { Input, ProvidersSelect, FormErrorLabel } from 'components/form';
import { Group, GroupContent, GroupItem } from 'components/common/Group';
import { required } from 'components/form/validationRules';

const useStyles = makeStyles((theme) => ({
  group: {
    paddingBottom: 0,
  },
  dialogRoot: {
    minWidth: 540,
  },
  dialogContent: {
    padding: '0 24px',
  },
  sellingChannelContainer: {
    borderRadius: '16px',
    padding: '20px',
    backgroundColor: '#FAFAFC',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  sellingChannelInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  marketLogo: {
    width: 38,
    height: 38,
  },
  updateChannelTitle: {
    fontSize: '17px',
    fontWeight: 700,
  },
  updateChannelSubtitle: {
    fontSize: '15px',
    color: '#969AB2',
  },
}));

const useMarkAsShipped = () => {
  const [open, setOpen] = useState<boolean>(false);
  const [orderId, setOrderId] = useState<string>();

  const handleOpen = (id: string) => {
    setOpen(true);
    setOrderId(id);
  };

  const handleClose = (cb?: () => void) => {
    setOpen(false);
    setOrderId(undefined);
    cb?.();
  };

  return { open, onOpen: handleOpen, onClose: handleClose, orderId };
};

interface Provider {
  code: string;
  fullName: string;
  logoUrl: string;
  smallLogoUrl: string;
  logo26Url: string;
  visible: true;
  enabled: true;
}

type MarkAsShippedDialogProps = {
  open: boolean;
  onClose: (cb?: () => void) => void;
  orderId?: string;
  provider?: Provider;
};

interface FormValues {
  provider: string;
  trackingNumber: string;
  updateSellingChannel: boolean;
}

export const MarkAsShippedDialog = ({
  open,
  onClose,
  orderId,
  provider,
}: MarkAsShippedDialogProps) => {
  const classes = useStyles();
  const formRef = useRef<FormApi<FormValues>>();

  const dataProvider = useDataProvider();
  const redirect = useRedirect();

  const submit = useCallback(
    async (data) => {
      try {
        const {
          data: { id },
        } = await dataProvider.put('markOrderShipped', { id: orderId, data });

        onClose();
        redirect('show', '/shipments', id);
        formRef.current?.reset();

        return;
      } catch (error: any) {
        return { [FORM_ERROR]: error?.message };
      }
    },
    [dataProvider, onClose, orderId, redirect]
  );

  return (
    <Form<FormValues>
      onSubmit={submit}
      initialValues={{
        updateSellingChannel: false,
        provider: '',
        trackingNumber: '',
      }}
      render={({ handleSubmit, form, submitting, values, submitError, ...renderProps }) => {
        formRef.current = form;

        return (
          <Dialog
            title="Mark Order as Shipped"
            open={open}
            confirmDisabled={submitting}
            onConfirm={handleSubmit}
            confirmUppercase={false}
            confirmText="Mark as Shipped"
            classes={{ root: classes.dialogRoot, content: classes.dialogContent }}
            onCancel={() => onClose(form.reset)}>
            <form onSubmit={handleSubmit}>
              {provider?.code !== 'RolloApp' && (
                <Box className={classes.sellingChannelContainer}>
                  <Box className={classes.sellingChannelInfo}>
                    {!!provider?.smallLogoUrl && (
                      <img
                        className={classes.marketLogo}
                        src={provider?.smallLogoUrl}
                        alt="market"
                      />
                    )}
                    <Box>
                      <Typography className={classes.updateChannelTitle}>
                        Update selling channel status
                      </Typography>
                      <Typography className={classes.updateChannelSubtitle}>
                        Mark as shipped on the original channel.
                      </Typography>
                    </Box>
                  </Box>
                  <Switch name="updateSellingChannel" inputProps={{ disabled: submitting }} />
                </Box>
              )}

              <Group classes={{ group: classes.group }}>
                <GroupContent>
                  <GroupItem>
                    <ProvidersSelect name="provider" label="Provider" validate={required} />
                  </GroupItem>
                  <GroupItem>
                    <Input
                      name="trackingNumber"
                      label={
                        values.updateSellingChannel
                          ? 'Tracking number'
                          : 'Tracking number (optional)'
                      }
                      validate={values.updateSellingChannel ? required : undefined}
                    />
                  </GroupItem>
                </GroupContent>
              </Group>

              <FormErrorLabel {...renderProps} submitError={submitError} />
            </form>
          </Dialog>
        );
      }}
    />
  );
};

export const MarkAsShippedActionField = (props) => {
  const {
    record: { id, marketAccount },
  } = props;

  const { open, orderId, onOpen, onClose } = useMarkAsShipped();

  return (
    <>
      <MenuItem onClick={() => onOpen(id)}>Mark as Shipped</MenuItem>
      {!!open && (
        <MarkAsShippedDialog
          open={open}
          orderId={orderId}
          onClose={onClose}
          provider={marketAccount?.provider}
        />
      )}
    </>
  );
};

export const MarkAsShippedActionButton = ({ id, data }) => {
  const { open, orderId, onOpen, onClose } = useMarkAsShipped();

  return (
    <>
      <ActionButton onClick={() => onOpen(id)}>Mark as Shipped</ActionButton>
      {!!open && (
        <MarkAsShippedDialog
          open={open}
          orderId={orderId}
          onClose={onClose}
          provider={data?.marketAccount?.provider}
        />
      )}
    </>
  );
};
